import { lazy } from 'react';
import { Navigate } from 'react-router-dom';
import AppLayout from './components/AppLayout';
import ProtectedRoute from './components/ProtectedRoute';

// Lazy-loaded route components
const LandingPage = lazy(() => import('./components/LandingPage'));
const LoginScreen = lazy(() => import('./components/LoginScreen'));
const StudentAuth = lazy(() => import('./components/StudentAuth'));
const TeacherAuth = lazy(() => import('./components/TeacherAuth'));
const TeacherVerification = lazy(() => import('./components/TeacherVerification'));
const ShopPage = lazy(() => import('./components/ShopPage'));
const ProductDetail = lazy(() => import('./components/ProductDetail'));
const CheckoutPage = lazy(() => import('./components/CheckoutPage'));
const UserProfileView = lazy(() => import('./components/UserProfileView'));
const StudentDashboard = lazy(() => import('./components/StudentDashboard'));
const TeacherDashboard = lazy(() => import('./components/TeacherDashboard'));
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const ChildDashboard = lazy(() => import('./components/ChildDashboard'));
const PresentationEditorView = lazy(() => import('./components/PresentationEditorView'));
const ProgramEditor = lazy(() => import('./components/ProgramEditor'));
const HomeworkEditor = lazy(() => import('./components/HomeworkEditor'));
const ModuleTestEditor = lazy(() => import('./components/ModuleTestEditor'));
const LessonGamesEditor = lazy(() => import('./components/LessonGamesEditor'));
const StudentHomeworkViewer = lazy(() => import('./components/StudentHomeworkViewer'));
const StudentModuleTestViewer = lazy(() => import('./components/StudentModuleTestViewer'));
const OnboardingPage = lazy(() => import('./components/OnboardingPage'));
const ReaderApp = lazy(() => import('./components/reader/ReaderApp'));
const BookDetailPage = lazy(() => import('./components/reader/BookDetailPage'));
const ReadingView = lazy(() => import('./components/reader/ReadingView'));
const NotFound = lazy(() => import('./components/NotFound'));
const DictionaryIndexPage = lazy(() => import('./components/dictionary/DictionaryIndexPage'));
const DictionaryWordPage = lazy(() => import('./components/dictionary/DictionaryWordPage'));
const KidsPlacementTest = lazy(() => import('./components/placement-test/KidsPlacementTest'));
const DevPresentationPreview = lazy(() => import('./components/DevPresentationPreview'));
const DevClassroom = lazy(() => import('./components/DevClassroom'));
const DevLevelTest = lazy(() => import('./components/DevLevelTest'));
const RecordingView = lazy(() => import('./components/RecordingView'));

export const routeConfig = [
  {
    element: <AppLayout />,
    children: [
      // Public routes
      { index: true, element: <LandingPage /> },
      { path: 'login', element: <LoginScreen /> },
      { path: 'register', element: <StudentAuth /> },
      { path: 'teacher-login', element: <TeacherAuth /> },
      { path: 'teacher-verify', element: <TeacherVerification /> },
      { path: 'shop', element: <ShopPage /> },
      { path: 'shop/:productId', element: <ProductDetail /> },
      { path: 'checkout', element: <CheckoutPage /> },
      { path: 'profile/:userId', element: <UserProfileView /> },

      // Public dictionary (SEO-facing)
      { path: 'dictionary', element: <DictionaryIndexPage /> },
      { path: 'dictionary/:word', element: <DictionaryWordPage /> },

      // Placement test (kids level test). Quick mode is public — anonymous
      // visitors can take it from the landing page. Full mode is gated by
      // a redirect inside the component (no ProtectedRoute wrapper so the
      // quick mode stays reachable without auth).
      { path: 'placement-test/:mode', element: <KidsPlacementTest /> },

      // Dev-only public preview of the lesson player for a given presentation.
      // No auth wrapper — local QA shortcut, do not expose in prod.
      { path: 'dev/preview/presentation/:id', element: <DevPresentationPreview /> },
      { path: 'dev/classroom', element: <DevClassroom /> },
      { path: 'dev/level-test/:mode', element: <DevLevelTest /> },

      // Composition view loaded by LiveKit Web Egress in a headless browser to
      // produce the lesson recording. NOT a user-facing route — reachable only
      // with a short-lived room token in `?token=`. See services/recordingService.
      { path: 'recording-view/:lessonId', element: <RecordingView /> },

      // Admin dashboard - protected
      {
        path: 'dashboard/admin',
        element: (
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminDashboard />
          </ProtectedRoute>
        ),
      },

      // Protected: student onboarding
      {
        path: 'onboarding',
        element: (
          <ProtectedRoute allowedRoles={['student']}>
            <OnboardingPage />
          </ProtectedRoute>
        ),
      },

      // Protected: student
      {
        path: 'dashboard/student',
        element: (
          <ProtectedRoute allowedRoles={['student']}>
            <StudentDashboard />
          </ProtectedRoute>
        ),
      },
      {
        path: 'dashboard/child/:childId',
        element: (
          <ProtectedRoute allowedRoles={['student']}>
            <ChildDashboard />
          </ProtectedRoute>
        ),
      },
      {
        path: 'homework/:id',
        element: (
          <ProtectedRoute allowedRoles={['student', 'teacher', 'admin']}>
            <StudentHomeworkViewer />
          </ProtectedRoute>
        ),
      },

      // Protected: teacher
      {
        path: 'dashboard/teacher',
        element: (
          <ProtectedRoute allowedRoles={['teacher']}>
            <TeacherDashboard />
          </ProtectedRoute>
        ),
      },

      // Protected: teacher or admin
      {
        path: 'editor/presentation/:id',
        element: (
          <ProtectedRoute allowedRoles={['teacher', 'admin']}>
            <PresentationEditorView />
          </ProtectedRoute>
        ),
      },
      {
        path: 'editor/program/:id',
        element: (
          <ProtectedRoute allowedRoles={['admin']}>
            <ProgramEditor />
          </ProtectedRoute>
        ),
      },
      {
        path: 'editor/homework/:id',
        element: (
          <ProtectedRoute allowedRoles={['teacher', 'admin']}>
            <HomeworkEditor />
          </ProtectedRoute>
        ),
      },
      {
        path: 'editor/module-test/:id',
        element: (
          <ProtectedRoute allowedRoles={['admin']}>
            <ModuleTestEditor />
          </ProtectedRoute>
        ),
      },
      {
        path: 'editor/lesson/:lessonId/games',
        element: (
          <ProtectedRoute allowedRoles={['admin']}>
            <LessonGamesEditor />
          </ProtectedRoute>
        ),
      },
      {
        path: 'editor/teacher-lesson/:teacherLessonId/games',
        element: (
          <ProtectedRoute allowedRoles={['teacher', 'admin']}>
            <LessonGamesEditor />
          </ProtectedRoute>
        ),
      },
      {
        path: 'module-test/:id',
        element: (
          <ProtectedRoute allowedRoles={['student', 'teacher', 'admin']}>
            <StudentModuleTestViewer />
          </ProtectedRoute>
        ),
      },

      // Reader (book-reading PWA) — library and chapter-1 preview are open
      // to anonymous visitors. Personal features (progress, bookmarks,
      // adding to vocabulary) prompt for login inside the components.
      { path: 'reader', element: <ReaderApp /> },
      { path: 'reader/book/:id', element: <BookDetailPage /> },
      { path: 'reader/book/:id/chapter/:num', element: <ReadingView /> },

      // Catch-all
      { path: '*', element: <NotFound /> },
    ],
  },
];

// Route table used when the app is served from the Reader subdomain
// (reader.lingvishspace.com). Mounts only the book-reading flow plus the
// login screens needed to authenticate before installing the device token.
// Internal Reader navigation still uses /reader/* paths (matching the
// main-domain mount) so no Reader component code changes; the subdomain
// root just redirects to /reader.
export const readerRouteConfig = [
  {
    element: <AppLayout />,
    children: [
      { index: true, element: <Navigate to="/reader" replace /> },
      { path: 'login', element: <LoginScreen /> },
      // "Увійти як Учень" on the login screen routes to /register
      // (StudentAuth handles both register and login modes via state.mode).
      { path: 'register', element: <StudentAuth /> },
      { path: 'teacher-login', element: <TeacherAuth /> },
      {
        path: 'onboarding',
        element: (
          <ProtectedRoute allowedRoles={['student']}>
            <OnboardingPage />
          </ProtectedRoute>
        ),
      },
      { path: 'reader', element: <ReaderApp /> },
      { path: 'reader/book/:id', element: <BookDetailPage /> },
      { path: 'reader/book/:id/chapter/:num', element: <ReadingView /> },
      { path: '*', element: <Navigate to="/reader" replace /> },
    ],
  },
];
